/**
 * Plan backgrounds: images are used as they are; PDFs (architect plans) are rendered to a PNG
 * at 150 dpi with pdf.js, loaded only when a PDF is imported.
 */
export interface ImportedPlan {
  bytes: Uint8Array;
  mime: 'image/png' | 'image/jpeg';
  widthPx: number;
  heightPx: number;
  /** Physical size if known (PDF pages), in mm. */
  widthMm?: number;
  heightMm?: number;
}

const MAX_PX = 6000;

async function imageSize(bytes: Uint8Array, mime: string): Promise<{ w: number; h: number }> {
  const bmp = await createImageBitmap(new Blob([bytes.slice().buffer as ArrayBuffer], { type: mime }));
  const size = { w: bmp.width, h: bmp.height };
  bmp.close();
  return size;
}

async function reencode(bytes: Uint8Array, mime: string): Promise<ImportedPlan> {
  // Normalise other formats (webp, gif…) to PNG so the PDF export can embed them.
  const bmp = await createImageBitmap(new Blob([bytes.slice().buffer as ArrayBuffer], { type: mime }));
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('image conversion failed');
  return {
    bytes: new Uint8Array(await blob.arrayBuffer()),
    mime: 'image/png',
    widthPx: bmp.width,
    heightPx: bmp.height,
  };
}

async function renderPdf(bytes: Uint8Array): Promise<ImportedPlan> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ data: bytes.slice() });
  const doc = await task.promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 }); // 72 dpi
    const scale = Math.min(150 / 72, MAX_PX / Math.max(base.width, base.height));
    const vp = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vp.width);
    canvas.height = Math.round(vp.height);
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: ctx, viewport: vp }).promise;
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    if (!blob) throw new Error('PDF rendering failed');
    return {
      bytes: new Uint8Array(await blob.arrayBuffer()),
      mime: 'image/png',
      widthPx: canvas.width,
      heightPx: canvas.height,
      widthMm: (base.width / 72) * 25.4,
      heightMm: (base.height / 72) * 25.4,
    };
  } finally {
    void task.destroy();
  }
}

export async function importPlan(name: string, bytes: Uint8Array): Promise<ImportedPlan> {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return renderPdf(bytes);
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
    const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
    const { w, h } = await imageSize(bytes, mime);
    return { bytes, mime, widthPx: w, heightPx: h };
  }
  return reencode(bytes, `image/${ext === 'svg' ? 'svg+xml' : ext}`);
}

export const PLAN_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'dxf', 'dwg'];
export const isCadFile = (name: string) => /\.(dxf|dwg)$/i.test(name);
